"use client"

import { CheckCircle, AlertCircle, Info, Bell, Calendar } from "lucide-react"
import type { Notification } from "@/lib/notifications-data"

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead?: (id: string) => void
  onActionClick?: (notification: Notification) => void
}

export function NotificationItem({ notification, onMarkAsRead, onActionClick }: NotificationItemProps) {
  const handleClick = () => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id)
    }
    if (notification.action && onActionClick) {
      onActionClick(notification)
    }
  }

  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case "warning":
        return <AlertCircle className="w-5 h-5 text-amber-500" />
      case "info":
        return <Info className="w-5 h-5 text-blue-500" />
      case "system":
        return <Bell className="w-5 h-5 text-purple-500" />
      default:
        return <Bell className="w-5 h-5 text-gray-500" />
    }
  }

  const getBackgroundColor = () => {
    switch (notification.type) {
      case "success":
        return "bg-green-50 border-green-200 hover:border-green-300"
      case "warning":
        return "bg-amber-50 border-amber-200 hover:border-amber-300"
      case "info":
        return "bg-blue-50 border-blue-200 hover:border-blue-300"
      case "system":
        return "bg-purple-50 border-purple-200 hover:border-purple-300"
      default:
        return "bg-gray-50 border-gray-200"
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`
        relative p-4 rounded-lg border cursor-pointer transition-all
        hover:shadow-md hover:scale-[1.01]
        ${getBackgroundColor()}
        ${!notification.read ? "ring-1 ring-blue-300" : ""}
      `}
    >
      {/* Unread indicator */}
      {!notification.read && <div className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />}

      <div className="flex gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 mt-1">{getIcon()}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm mb-1">{notification.title}</h3>
          <p className="text-gray-600 text-sm mb-3 leading-relaxed">{notification.message}</p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>{notification.time}</span>
            </div>

            {notification.action && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (onActionClick) onActionClick(notification)
                }}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 underline"
              >
                {notification.action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
